import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { MessageCircle, Sparkles, Shield, Zap, Globe, ChevronRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

/*
  /aiagent — rebuilt as a semantic capability map for Roni.

  Goal: give Google + Bing + AI answer engines a rich, hierarchical
  index of every real-world question Roni is designed to answer, so the
  page ranks for the underlying intents (not the bot's name). The 5
  categories × ~15-20 questions structure is derived from Google's
  guidance against thin/duplicate scaled content: every question is a
  distinct intent, not a keyword permutation.

  Deliberately NOT using FAQPage schema for the question list because
  Google's current guidance restricts QAPage/FAQPage to pages focused
  on answering a single question (per Sept 2026 Search Central docs).
  Instead: WebApplication + Organization + WebPage.mainEntity graph.

  Clicking any question opens Roni and fires a `roni:prefill` event —
  safe no-op if the widget doesn't yet listen. SEO value is the DOM
  content itself, prefill UX is progressive enhancement.
*/

// -------- 5 category × ~17 question semantic map (86 total) --------
const QUESTION_INDEX = [
  {
    slug: "prices",
    title_en: "Dubai Property Prices",
    title_ar: "أسعار عقارات دبي",
    intro_en: "AED per square foot, apartment and villa medians, community-level pricing, historical price change.",
    intro_ar: "أسعار القدم المربّعة بالدرهم، والقيم الوسيطة لأسعار الشقق والفلل، والأسعار بحسب المنطقة، والتغيّر التاريخي في الأسعار.",
    questions: [
      { en: "What is the average apartment price in Dubai?", ar: "ما هو متوسط سعر الشقة في دبي؟" },
      { en: "What is the median price per square foot in Dubai?", ar: "ما هو وسيط سعر القدم المربّع في دبي؟" },
      { en: "Which Dubai communities have the highest AED per sqft?", ar: "أي مجتمعات دبي لها أعلى سعر للقدم المربّع؟" },
      { en: "Which Dubai communities are the most affordable?", ar: "أي مجتمعات دبي الأكثر تناسباً للميزانية؟" },
      { en: "How much property does AED 1M buy in Dubai?", ar: "ماذا يشتري مليون درهم في دبي؟" },
      { en: "How much property does AED 2M buy in Dubai?", ar: "ماذا يشتري مليونا درهم في دبي؟" },
      { en: "How much property does AED 3M buy in Dubai?", ar: "ماذا يشتري ثلاثة ملايين درهم في دبي؟" },
      { en: "What are Palm Jumeirah apartment prices?", ar: "ما أسعار شقق نخلة جميرا؟" },
      { en: "What are Dubai Marina apartment prices?", ar: "ما أسعار شقق دبي مارينا؟" },
      { en: "What are Business Bay apartment prices?", ar: "ما أسعار شقق الخليج التجاري؟" },
      { en: "What are Dubai Hills Estate villa prices?", ar: "ما أسعار فلل دبي هيلز؟" },
      { en: "What are Downtown Dubai penthouse prices?", ar: "ما أسعار البنتهاوس في وسط مدينة دبي؟" },
      { en: "How have Dubai prices changed month over month in 2026?", ar: "كيف تغيّرت أسعار دبي شهرياً في ٢٠٢٦؟" },
      { en: "How have Dubai prices changed year over year 2025 versus 2026?", ar: "كيف تغيّرت أسعار دبي بين ٢٠٢٥ و٢٠٢٦؟" },
      { en: "What is the largest sale on record in Dubai this year?", ar: "ما أكبر صفقة مسجّلة في دبي هذا العام؟" },
      { en: "Which sales exceeded AED 100M this month in Dubai?", ar: "أي صفقات تخطّت مئة مليون درهم هذا الشهر؟" },
      { en: "Are off plan or ready properties more expensive in Dubai?", ar: "هل العقارات قيد الإنشاء أغلى أم الجاهزة؟" },
      { en: "What is the biggest Dubai sale this week?", ar: "ما أكبر صفقة في دبي هذا الأسبوع؟" },
    ],
  },
  {
    slug: "transactions",
    title_en: "Dubai DLD Transactions",
    title_ar: "صفقات دائرة الأراضي والأملاك",
    intro_en: "Volume, value, off plan versus ready, procedure types, weekly and monthly DLD data.",
    intro_ar: "الحجم والقيمة، قيد الإنشاء مقابل الجاهز، أنواع الإجراءات، بيانات دائرة الأراضي الأسبوعية والشهرية.",
    questions: [
      { en: "How many property transactions occurred in Dubai this year?", ar: "كم عدد الصفقات العقارية في دبي هذا العام؟" },
      { en: "How many Dubai transactions were sales versus mortgages?", ar: "كم من الصفقات كانت بيعاً مقابل الرهون؟" },
      { en: "What is the total value of Dubai property transactions in 2026?", ar: "ما القيمة الإجمالية للصفقات العقارية في دبي ٢٠٢٦؟" },
      { en: "Which Dubai communities had the highest transaction volume?", ar: "أي مجتمعات دبي سجّلت أعلى عدد صفقات؟" },
      { en: "Which weeks had the highest sales value in Dubai 2026?", ar: "أي أسابيع سجّلت أعلى قيمة مبيعات في دبي ٢٠٢٦؟" },
      { en: "What percentage of Dubai sales are off plan in 2026?", ar: "ما نسبة مبيعات دبي قيد الإنشاء في ٢٠٢٦؟" },
      { en: "What percentage of Dubai sales are ready secondary in 2026?", ar: "ما نسبة مبيعات الوحدات الجاهزة الثانوية في ٢٠٢٦؟" },
      { en: "Where can I find DLD open data?", ar: "من أين أستطيع الوصول إلى البيانات المفتوحة لدائرة الأراضي؟" },
      { en: "How often is DLD data updated?", ar: "كم مرة تُحدَّث بيانات دائرة الأراضي؟" },
      { en: "What is the DLD transfer fee?", ar: "ما رسم نقل الملكية لدى دائرة الأراضي؟" },
      { en: "What does DLD Oqood registration mean?", ar: "ماذا يعني تسجيل أوقود لدى دائرة الأراضي؟" },
      { en: "Who handles DLD title transfers in Dubai?", ar: "من يُدير عمليات نقل الملكية لدى دائرة الأراضي؟" },
      { en: "What are the latest weekly DLD figures?", ar: "ما آخر أرقام دائرة الأراضي الأسبوعية؟" },
      { en: "How does H1 2026 compare with H1 2025 in Dubai?", ar: "كيف يقارن النصف الأول ٢٠٢٦ مع النصف الأول ٢٠٢٥؟" },
      { en: "Which developer sold the most units this month in Dubai?", ar: "أي مطوّر باع أكبر عدد وحدات هذا الشهر في دبي؟" },
      { en: "What is the difference between DLD transactions and DLD procedures?", ar: "ما الفرق بين صفقات دائرة الأراضي وإجراءاتها؟" },
    ],
  },
  {
    slug: "rentals",
    title_en: "Dubai Rental Market & Yields",
    title_ar: "سوق الإيجارات والعوائد",
    intro_en: "Rents by community, gross versus net yield, service charges, RERA Smart Rental Index.",
    intro_ar: "الإيجارات حسب المجتمع، العائد الإجمالي مقابل الصافي، رسوم الخدمة، مؤشّر ريرا للإيجارات.",
    questions: [
      { en: "What is the gross rental yield in Dubai Marina?", ar: "ما العائد الإجمالي للإيجار في دبي مارينا؟" },
      { en: "What is the gross rental yield in JVC?", ar: "ما العائد الإجمالي للإيجار في قرية جميرا الدائرية؟" },
      { en: "What is the gross rental yield in Business Bay?", ar: "ما العائد الإجمالي للإيجار في الخليج التجاري؟" },
      { en: "Which Dubai communities have the highest rental yields?", ar: "أي مجتمعات دبي لها أعلى عوائد إيجارية؟" },
      { en: "How do I calculate net rental yield in Dubai?", ar: "كيف أحسب صافي عائد الإيجار في دبي؟" },
      { en: "How does gross yield differ from net yield?", ar: "كيف يختلف العائد الإجمالي عن العائد الصافي؟" },
      { en: "What is a realistic Dubai apartment service charge?", ar: "ما هو رسم الخدمة المعقول لشقة في دبي؟" },
      { en: "What are typical Dubai property management fees?", ar: "ما رسوم إدارة العقارات الاعتيادية في دبي؟" },
      { en: "What is the average 1BR rent in Dubai Marina?", ar: "ما متوسط إيجار شقة من غرفة نوم واحدة في دبي مارينا؟" },
      { en: "What is the average 2BR rent in Downtown Dubai?", ar: "ما متوسط إيجار شقة من غرفتي نوم في وسط مدينة دبي؟" },
      { en: "How does the RERA Smart Rental Index work?", ar: "كيف يعمل مؤشّر ريرا الذكي للإيجارات؟" },
      { en: "What are Dubai eviction notice rules?", ar: "ما قواعد إخطار الإخلاء في دبي؟" },
      { en: "How much can a Dubai landlord legally raise the rent?", ar: "كم يستطيع المالك رفع الإيجار قانونياً في دبي؟" },
      { en: "What is the typical vacancy rate for Dubai apartments?", ar: "ما معدّل الشغور الاعتيادي لشقق دبي؟" },
    ],
  },
  {
    slug: "investment",
    title_en: "Investment, Financing & Visa",
    title_ar: "الاستثمار والتمويل وتأشيرة الإقامة",
    intro_en: "Golden Visa, CBUAE mortgage rules, all in acquisition cost, ownership structures, corporate tax.",
    intro_ar: "الإقامة الذهبية، قواعد الرهن العقاري لدى المصرف المركزي، التكلفة الكلية، هياكل التملّك، ضريبة الشركات.",
    questions: [
      { en: "Is AED 2 million enough for a Dubai investment property?", ar: "هل يكفي مليونا درهم لشراء عقار استثماري في دبي؟" },
      { en: "What is the UAE Golden Visa property threshold?", ar: "ما الحدّ الأدنى للتأشيرة الذهبية عبر العقار؟" },
      { en: "How do I qualify for the Dubai Golden Visa via property?", ar: "كيف أستحق التأشيرة الذهبية عبر عقار في دبي؟" },
      { en: "What are the CBUAE mortgage LTV rules in 2026?", ar: "ما قواعد نسبة القرض إلى القيمة لدى المصرف المركزي ٢٠٢٦؟" },
      { en: "Can non residents get a Dubai mortgage?", ar: "هل يستطيع غير المقيمين الحصول على رهن عقاري في دبي؟" },
      { en: "What is the maximum LTV for off plan property in the UAE?", ar: "ما الحدّ الأقصى لنسبة القرض إلى القيمة قيد الإنشاء؟" },
      { en: "What are typical Dubai mortgage interest rates in 2026?", ar: "ما أسعار الفائدة المعتادة على الرهن في دبي ٢٠٢٦؟" },
      { en: "How do I calculate the all in acquisition cost of a Dubai property?", ar: "كيف أحسب التكلفة الكلية لشراء عقار في دبي؟" },
      { en: "What are the total buyer costs when purchasing in Dubai?", ar: "ما إجمالي تكاليف المشتري عند الشراء في دبي؟" },
      { en: "What is the Dubai developer default protection law?", ar: "ما قانون حماية المشتري من إخلال المطوّر في دبي؟" },
      { en: "How does UAE Corporate Tax apply to Dubai rental income?", ar: "كيف تُطبَّق ضريبة الشركات الإماراتية على الإيجارات؟" },
      { en: "Can I hold Dubai property through an offshore company?", ar: "هل يمكنني تملّك عقار دبي عبر شركة خارجية؟" },
      { en: "Can I use a JAFZA offshore company for Dubai property?", ar: "هل تصلح شركة جافزا الخارجية لتملّك عقار دبي؟" },
      { en: "What is a DIFC Prescribed Company?", ar: "ما هي شركة مركز دبي المالي المعتمدة؟" },
      { en: "What happens if a Dubai off plan project is cancelled?", ar: "ماذا يحدث إذا أُلغي مشروع قيد الإنشاء في دبي؟" },
      { en: "What is a RERA escrow account and why does it matter?", ar: "ما هو حساب الضمان لدى ريرا ولماذا يهم؟" },
      { en: "What is the 30 to 40 percent off plan resale rule?", ar: "ما قاعدة إعادة بيع الوحدات قيد الإنشاء عند ٣٠ إلى ٤٠ بالمئة؟" },
      { en: "How much cash do I need beyond the Dubai purchase price?", ar: "كم أحتاج من السيولة فوق سعر الشراء في دبي؟" },
    ],
  },
  {
    slug: "communities",
    title_en: "Community & Area Intelligence",
    title_ar: "المجتمعات والمناطق",
    intro_en: "Deep-dive summaries and comparisons across Dubai's 25 mapped freehold communities.",
    intro_ar: "ملخّصات ومقارنات معمّقة للمناطق المدرجة ضمن نطاق التغطية في دبي، مع توضيح نطاق التملّك الحرّ.",
    questions: [
      { en: "What are the key facts about Downtown Dubai?", ar: "ما أهم الحقائق عن وسط مدينة دبي؟" },
      { en: "What are the key facts about Dubai Marina?", ar: "ما أهم الحقائق عن دبي مارينا؟" },
      { en: "What are the key facts about Palm Jumeirah?", ar: "ما أهم الحقائق عن نخلة جميرا؟" },
      { en: "What are the key facts about JVC Jumeirah Village Circle?", ar: "ما أهم الحقائق عن قرية جميرا الدائرية؟" },
      { en: "What are the key facts about Business Bay?", ar: "ما أهم الحقائق عن الخليج التجاري؟" },
      { en: "What are the key facts about Dubai Hills Estate?", ar: "ما أهم الحقائق عن دبي هيلز؟" },
      { en: "What are the key facts about Arjan?", ar: "ما أهم الحقائق عن أرجان؟" },
      { en: "What are the key facts about Dubai South?", ar: "ما أهم الحقائق عن دبي الجنوب؟" },
      { en: "What are the key facts about Al Furjan?", ar: "ما أهم الحقائق عن الفرجان؟" },
      { en: "What are the key facts about JLT Jumeirah Lake Towers?", ar: "ما أهم الحقائق عن أبراج بحيرات جميرا؟" },
      { en: "What are the key facts about Discovery Gardens?", ar: "ما أهم الحقائق عن حدائق الاكتشاف؟" },
      { en: "What are the key facts about Damac Hills 2?", ar: "ما أهم الحقائق عن داماك هيلز ٢؟" },
      { en: "What are the key facts about Motor City?", ar: "ما أهم الحقائق عن موتور سيتي؟" },
      { en: "What are the key facts about Town Square?", ar: "ما أهم الحقائق عن تاون سكوير؟" },
      { en: "What are the key facts about Dubai Sports City?", ar: "ما أهم الحقائق عن مدينة دبي الرياضية؟" },
      { en: "What are the key facts about Dubai Creek Harbour?", ar: "ما أهم الحقائق عن ميناء خور دبي؟" },
      { en: "What are the key facts about International City?", ar: "ما أهم الحقائق عن المدينة العالمية؟" },
      { en: "How does JVC compare with Dubai Hills Estate?", ar: "كيف تقارن قرية جميرا الدائرية بدبي هيلز؟" },
      { en: "How does Dubai Marina compare with Downtown Dubai?", ar: "كيف تقارن دبي مارينا بوسط مدينة دبي؟" },
      { en: "Which Dubai community offers the best value under AED 2M?", ar: "أي مجتمع دبي يقدّم أفضل قيمة تحت مليوني درهم؟" },
    ],
  },
];

export default function AiAgent() {
  const { lang } = useLang();
  const isAR = lang === "ar";
  const canonical = isAR ? "https://thepropertic.ae/aiagent?lang=ar" : "https://thepropertic.ae/aiagent";
  const enUrl = "https://thepropertic.ae/aiagent";
  const arUrl = "https://thepropertic.ae/aiagent?lang=ar";

  // Meta pinned to Bing 120-160 char window. EN = 148, AR = 152.
  const seoTitle = isAR
    ? "روني · مستشار عقاري بالذكاء الاصطناعي | أسئلة عقار دبي"
    : "Roni AI Property Advisor | Dubai Real Estate Questions";
  const seoDesc = isAR
    ? "اسأل روني عن عقار دبي، بيانات السوق، الرهن العقاري، المناطق، واعتبارات الشراء. إجابات مبنيّة على البيانات مع المصادر وحدود المعلومات الواضحة."
    : "Ask Roni about Dubai property, market data, mortgages, areas and buying considerations. Data-informed answers with sources and clear limitations.";

  // Auto-open the floating chat panel on this page, and scrub any legacy
  // "#ai_agent" fragment left over from old sitelinks so the URL stays clean
  // (/aiagent instead of /aiagent#ai_agent). We fire a custom event that
  // AIChatWidget listens for, no URL mutation.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.hash) {
      // Preserve query string (e.g. ?lang=ar), drop only the fragment.
      const clean = window.location.pathname + window.location.search;
      window.history.replaceState(null, "", clean);
    }
    window.dispatchEvent(new CustomEvent("roni:open"));
  }, []);

  const openRoniWith = (questionText) => {
    if (typeof window === "undefined") return;
    // Dispatches "roni:ask" so the chat panel opens AND auto-submits the
    // question, Roni streams the answer without a second user tap.
    window.dispatchEvent(new CustomEvent("roni:ask", { detail: { text: questionText } }));
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebApplication",
        "@id": `${enUrl}#roni`,
        "name": "Roni, The Propertic AI Property Agent",
        "url": enUrl,
        "applicationCategory": "BusinessApplication",
        "operatingSystem": "Any (web)",
        "inLanguage": ["en", "ar"],
        "offers": { "@type": "Offer", "price": "0", "priceCurrency": "AED" },
        "provider": { "@id": "https://thepropertic.ae/#organization" },
        "featureList": [
          "Dubai property price benchmarks by community",
          "DLD transaction volume and value analysis",
          "Rental yields, gross and net calculators",
          "CBUAE mortgage LTV rules for nationals, residents, non residents",
          "10-year UAE Golden Visa pathway guidance (subject to ICP/DLD eligibility)",
          "Community and area intelligence across 44 freehold zones",
          "Multi currency quotes across AED, USD, GBP, EUR, RUB, INR, CNY, SAR",
          "Bilingual English and Arabic",
          "Every figure attributed to DLD, RERA, CBUAE or a named third party source",
        ],
      },
      {
        "@type": "WebPage",
        "@id": `${canonical}#page`,
        "url": canonical,
        "name": seoTitle,
        "description": seoDesc,
        "inLanguage": isAR ? "ar" : "en",
        "isPartOf": { "@id": "https://thepropertic.ae/#website" },
        "about": { "@id": `${enUrl}#roni` },
        "primaryImageOfPage": { "@type": "ImageObject", "url": "https://thepropertic.ae/og-share-logo.png" },
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": isAR ? "الرئيسية" : "Home", "item": "https://thepropertic.ae/" },
          { "@type": "ListItem", "position": 2, "name": isAR ? "وكيل الذكاء الاصطناعي" : "AI Agent", "item": "https://thepropertic.ae/aiagent" },
        ],
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="googlebot" content="index, follow, max-image-preview:large, max-snippet:-1" />
        <meta name="description" content={seoDesc} />
        <link rel="canonical" href={canonical} />
        <link rel="alternate" hrefLang="en" href={enUrl} />
        <link rel="alternate" hrefLang="ar" href={arUrl} />
        <link rel="alternate" hrefLang="x-default" href={enUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDesc} />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content="https://thepropertic.ae/og-share-logo.png" />
        <meta property="og:locale" content={isAR ? "ar_AE" : "en_AE"} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDesc} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* ============ HERO ============ */}
      <section className="pt-24 sm:pt-32 pb-10 sm:pb-14 relative overflow-hidden">
        <div className="blob-gold w-[500px] h-[500px] -top-40 -end-40 opacity-40" />
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 relative">
          <motion.p
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="font-mono-brand text-propertic-gold text-[10px] tracking-[0.24em] uppercase mb-3"
            data-testid="aiagent-eyebrow"
          >
            {isAR ? "وكيل الذكاء الاصطناعي · روني" : "AI Property Agent · Roni"}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.05 }}
            className="font-editorial text-3xl sm:text-4xl lg:text-5xl text-propertic-navy leading-[1.15]"
            data-testid="aiagent-title"
          >
            {isAR ? (
              <>روني <span className="font-italic-serif text-propertic-gold">وكيل بروبيرتك للذكاء الاصطناعي العقاري.</span></>
            ) : (
              <>Roni <span className="font-italic-serif text-propertic-gold">Propertic's AI property intelligence agent.</span></>
            )}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-6 text-propertic-ink-2/95 text-[15px] sm:text-lg leading-[1.75] max-w-5xl"
          >
            {isAR
              ? "اسأل روني عن أسعار عقارات دبي، وصفقات دائرة الأراضي، والإيجارات والعوائد، والمجتمعات، وتحليلات الاستثمار، واتجاهات السوق العقاري."
              : "Ask Roni about Dubai property prices, DLD transactions, rents and yields, communities, investment analysis and real estate market trends."}
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.22 }}
            className="mt-4 text-propertic-ink-2/85 text-[14px] sm:text-[15px] leading-[1.7] max-w-5xl"
          >
            {isAR
              ? "يعتمد روني على بيانات بروبيرتك المنظّمة ومعلومات السوق ليجيب على أسئلة العقارات ببيانات حديثة. الأرقام الرسمية تُنسب إلى مصادرها (دائرة الأراضي والأملاك، ومؤسّسة التنظيم العقاري (ريرا)، والمصرف المركزي الإماراتي)؛ أما المؤشرات المحسوبة (العوائد، والتغيّر السنوي، والحصص) فتُحتسب من قِبل بروبيرتك مع إظهار المنهجية؛ وعند استخدام مدخلات من طرف ثالث لإكمال الإجابة، يُصرّح روني بمصدرها في الرد نفسه."
              : "Roni uses Propertic's structured property data and market intelligence to answer questions with current information. Official-source figures are cited (Dubai Land Department, RERA, Central Bank of the UAE); derived metrics (yields, YoY, share%) are calculated by The Propertic and labelled with their formula; when a third-party input completes an answer, Roni discloses that source inline with the reply."}
          </motion.p>

          <motion.button
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }}
            onClick={() => window.dispatchEvent(new CustomEvent("roni:open"))}
            data-testid="aiagent-open-cta"
            className="mt-8 inline-flex items-center gap-2 h-12 px-6 rounded-full bg-propertic-navy text-propertic-paper hover:bg-propertic-navy/90 hover:shadow-lg font-mono-brand text-xs tracking-[0.14em] uppercase transition-all"
          >
            <MessageCircle className="w-4 h-4" aria-hidden="true" />
            {isAR ? "ابدأ الدردشة مع روني" : "Start chatting with Roni"}
          </motion.button>
        </div>
      </section>

      {/* ============ QUESTIONS INDEX ============ */}
      <section className="pb-12 sm:pb-16 relative" data-testid="aiagent-question-index">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <p className="font-mono-brand text-propertic-gold text-[10px] tracking-[0.24em] uppercase mb-3">
            {isAR ? "خريطة قدرات روني" : "Roni's capability map"}
          </p>
          <h2 className="font-editorial text-2xl sm:text-3xl text-propertic-navy leading-tight mb-3">
            {isAR ? (
              <>الأسئلة التي يستطيع <span className="font-italic-serif text-propertic-gold">روني</span> الإجابة عنها</>
            ) : (
              <>Questions <span className="font-italic-serif text-propertic-gold">Roni</span> can answer</>
            )}
          </h2>
          <p className="text-propertic-ink-2/85 text-[14.5px] leading-relaxed max-w-5xl mb-10">
            {isAR
              ? "٨٦ سؤالاً فعلياً موزَّعة على خمس فئات. اضغط على أي سؤال ليفتح روني مسبقاً بالسؤال جاهزاً."
              : "86 real-world questions across five categories. Tap any question to open Roni with that question preloaded."}
          </p>

          <div className="space-y-10">
            {QUESTION_INDEX.map((cat, idx) => (
              <div key={cat.slug} data-testid={`aiagent-category-${cat.slug}`}>
                <div className="flex items-baseline gap-3 mb-2">
                  <span className="font-mono-brand text-propertic-gold text-[10px] tracking-[0.2em] tabular">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <h3 className="font-editorial text-xl sm:text-2xl text-propertic-navy leading-tight">
                    {isAR ? cat.title_ar : cat.title_en}
                  </h3>
                </div>
                <p className="text-[13.5px] text-propertic-ink-2/80 leading-relaxed mb-5 max-w-5xl">
                  {isAR ? cat.intro_ar : cat.intro_en}
                </p>
                <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                  {cat.questions.map((q, qi) => (
                    <li key={qi}>
                      <button
                        type="button"
                        onClick={() => openRoniWith(isAR ? q.ar : q.en)}
                        data-testid={`aiagent-question-${cat.slug}-${qi}`}
                        className="group w-full text-start rounded-xl border hairline bg-white/70 hover:bg-white hover:border-propertic-gold/60 transition-colors px-4 py-3 flex items-start justify-between gap-2"
                      >
                        <span className="text-[13px] text-propertic-ink leading-snug">
                          {isAR ? q.ar : q.en}
                        </span>
                        <ChevronRight className="w-3.5 h-3.5 shrink-0 mt-0.5 text-propertic-gold/70 group-hover:text-propertic-gold transition-colors" aria-hidden="true" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FEATURE CARDS ============ */}
      <section className="pb-14 sm:pb-20 relative">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          <FeatureCard icon={Shield} title={isAR ? "مصادر موثّقة" : "Sourced answers"} body={isAR ? "الأرقام الصادرة عن الجهات الرسمية (دائرة الأراضي، والمصرف المركزي، ومؤسّسة التنظيم العقاري (ريرا)) تُنسب إلى مصادرها؛ أما المؤشرات المحسوبة والافتراضات فتُوضَّح بشكل صريح مع منهجيتها." : "Official-source figures (DLD, CBUAE, RERA) are cited with their reference; derived figures, calculations and assumptions are clearly labelled with the methodology."} />
          <FeatureCard icon={Globe} title={isAR ? "ثنائي اللغة" : "Bilingual"} body={isAR ? "اطرح أسئلتك بالعربية أو الإنجليزية بالجودة نفسها، واطّلع على الأرقام بالعملة التي تختارها." : "Ask in English or Arabic with equal fluency, quotes in your local currency."} />
          <FeatureCard icon={Zap} title={isAR ? "استجابة سريعة" : "Fast responses"} body={isAR ? "إجابات مفصّلة عن الأسعار، والرهن، ومسارات الإقامة." : "Detailed answers on prices, mortgage rules, and residency pathways."} />
          <FeatureCard icon={Sparkles} title={isAR ? "بلا تسجيل دخول" : "No login required"} body={isAR ? "الدردشة مجانية ومجهولة الهوية حتى تختار أنت طلب استشارة." : "Chat is free and anonymous until you choose to book a consultation."} />
        </div>
      </section>
    </>
  );
}

function FeatureCard({ icon: Icon, title, body }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
      className="rounded-2xl p-5 bg-white border hairline"
    >
      <div className="w-9 h-9 rounded-full grid place-items-center bg-propertic-gold/12 text-propertic-gold mb-3">
        <Icon className="w-4 h-4" aria-hidden="true" />
      </div>
      <p className="font-editorial text-lg text-propertic-navy mb-1.5">{title}</p>
      <p className="text-[13px] text-propertic-ink-2/85 leading-relaxed">{body}</p>
    </motion.div>
  );
}
